import React from 'react';
import { createContext, useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { BASE_API_ENDPOINT } from '../constants';
import { TileConfig } from '../types/floor';
import { useTargetContext } from './TargetContext';

export type TilesConfig = {
  [key: string]: TileConfig;
};

export type SignableConfig = {
  editRoomSignMessage: string;
  moveRoomSignMessage: string;
  enterRoomSignMessage: string;
};

export type VisitorLimitConfig = {
  maxVisitorLimit: number;
  minVisitorLimit: number;
};

export type ConfigurationContextType = {
  tilesConfig?: TilesConfig;
  signables: SignableConfig;
  visitorLimit: VisitorLimitConfig;
  lockedStates: string[];
  moveCost: number;
};

const defaultValue: ConfigurationContextType = {
  tilesConfig: undefined,
  signables: {
    editRoomSignMessage: '',
    moveRoomSignMessage: '',
    enterRoomSignMessage: '',
  },
  visitorLimit: {
    maxVisitorLimit: 75,
    minVisitorLimit: 10,
  },
  lockedStates: [],
  moveCost: 20,
};

export const ConfigurationContext =
  createContext<ConfigurationContextType>(defaultValue);

type Props = {
  children: any;
};

export const ConfigurationContextProvider = ({ children }: Props) => {
  const { target } = useTargetContext();
  const [tiles, setTiles] = useState(undefined);
  const [signables, setSignables] = useState<SignableConfig>(
    defaultValue.signables
  );
  const [visitorLimit, setVisitorLimit] = useState<VisitorLimitConfig>({
    maxVisitorLimit: 75,
    minVisitorLimit: 10,
  });
  const [lockedStates, setLockedStates] = useState<string[]>(
    defaultValue.lockedStates
  );
  const [moveCost, setMoveCost] = useState<number>(defaultValue.moveCost);

  useQuery(
    'configuration',
    () => {
      return fetch(`${BASE_API_ENDPOINT[target]}/explorer/configuration/`)
        .then(async (response) => {
          return await response.json();
        })
        .then((data) => {
          setSignables(data.signables);
          setTiles(data.tiles);
          setVisitorLimit({
            minVisitorLimit: data.minVisitorLimit,
            maxVisitorLimit: data.maxVisitorLimit,
          });
          setLockedStates(data.lockedStates);
          setMoveCost(data.moveCost);

          target === 'dev' && console.warn('configuration received:');
          target === 'dev' && console.log(data.tiles);
        });
    },
    { cacheTime: Infinity, retryOnMount: false, refetchOnWindowFocus: false }
  );

  return (
    <ConfigurationContext.Provider
      value={{
        tilesConfig: tiles,
        signables,
        visitorLimit,
        lockedStates,
        moveCost,
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
};

export function useConfigurationContext() {
  const context = useContext(ConfigurationContext);
  if (context === undefined) {
    throw new Error('Configuration context must be used within a Provider');
  }
  return context;
}
